// Registry of natural-language-queryable endpoints for the chat assistant.
//
// Each entry encapsulates everything the chat needs in order to:
//   1. recognise that a user message is asking for that dataset
//      ("positions", "budgets", "allocations", ...)
//   2. call the matching list endpoint with the parsed filters
//   3. render the results inside the chat drawer using the same
//      MUI table component the dedicated dashboard page uses
//   4. handle edit clicks by navigating to the full-page workflow
//
// The chat orchestrator (`handleQueryMessage` in ChatAssistant.tsx) walks
// this registry once per incoming message instead of hard-coding any
// dataset-specific branching, so wiring up a new endpoint is a single
// `descriptors.push({ ... })` change.
import React from 'react';
import type { NavigateFunction } from 'react-router';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import type {
    AllocationModel,
    BudgetModel,
    GetCustomerSettingsResponse,
    PositionModel,
    PositionType,
} from '../../../../Generated/Commodity-Management-Api';
import PositionTable from '../Positions/ViewPositionsPage/PositionTable';
import BudgetTable from '../Budgets/ViewBudgetsPage/BudgetTable';
import AllocationTable from '../Allocations/AllocationTable';
import type { BudgetRow } from '../Budgets/Types';
import type { PositionReportColumn } from '../Positions/ViewPositionsPage/Types';
import { PositionColumns } from '../Positions/ViewPositionsPage/Types';
import type { FiscalYearRange } from './ChatFiscalYear';

// Filters extracted from a user message. Each list endpoint consumes the
// subset that applies to it; the parser fills in whatever it can detect.
export interface ChatQueryFilters {
    commodityIds?: number[];
    commodityNames?: string[];
    locationIds?: number[];
    locationNames?: string[];
    counterpartyIds?: number[];
    counterpartyNames?: string[];
    portfolioIds?: number[];
    portfolioNames?: string[];
    fiscalYears?: number[];
    fiscalYearRanges?: FiscalYearRange[];
    tradeDateStart?: string;          // YYYY-MM-DD
    tradeDateEnd?: string;            // YYYY-MM-DD
    useTradeDate?: boolean;           // true when message explicitly mentions trade date
    positionTypes?: PositionType[];
    side?: 'Buy' | 'Sell';
}

// Context passed into render/handlers so the registry stays decoupled
// from React state inside ChatAssistant.tsx.
export interface ChatQueryContext {
    customerSettings: GetCustomerSettingsResponse;
    navigate: NavigateFunction;
    closeDrawer?: () => void;
}

export interface ChatQueryDescriptor {
    // Stable key used in observation/logging. Matches the dataset noun.
    key: 'positions' | 'budgets' | 'allocations';
    // User-facing dataset label (singular) used when describing results.
    singular: string;
    // User-facing dataset label (plural) used in confirmation messages.
    plural: string;
    // Keywords that, when present in the message, indicate this dataset.
    // First-match wins, so put more-specific keywords (e.g. "allocations")
    // before more-general ones (e.g. "positions"). The orchestrator picks
    // the descriptor with the highest weighted keyword score.
    keywords: string[];
    // Aliases that would otherwise mis-route, e.g. budget files often
    // also contain "positions". The orchestrator subtracts these when
    // scoring this descriptor.
    antiKeywords?: string[];
    // Execute the list endpoint with the parsed filters. Implementations
    // ignore filters they don't support and surface the raw rows; the
    // orchestrator handles error messages.
    execute(filters: ChatQueryFilters, ctx: ChatQueryContext): Promise<unknown[]>;
    // Render the rows inside the chat drawer's result panel. The element
    // returned should already be a full table-style component.
    render(rows: unknown[], ctx: ChatQueryContext): React.ReactElement;
    // Optional pretty summary helper used when no rows match. Kept on
    // the descriptor so dataset-specific phrasing stays close to the
    // dataset definition.
    emptyMessage?: (filters: ChatQueryFilters) => string;
}

// All positions table columns rendered in the chat drawer. The full
// ViewPositionsPage uses a customizable report config; for the chat we
// show every standard column so the user sees as much context as the
// API returned.
const ALL_POSITION_COLUMNS = Object.keys(PositionColumns) as PositionReportColumn[];

// Build commodity/location/counterparty id arrays from filters, preferring
// explicit ids and falling back to name-derived ids (parser fills both).
function pickIds(ids: number[] | undefined): number[] | undefined {
    return ids && ids.length > 0 ? ids : undefined;
}

// ---------------------------------------------------------------------------
// Positions
// ---------------------------------------------------------------------------
const positionsDescriptor: ChatQueryDescriptor = {
    key: 'positions',
    singular: 'position',
    plural: 'positions',
    keywords: ['position', 'positions', 'trade', 'trades', 'fill', 'fills', 'deal', 'deals'],
    antiKeywords: ['budget', 'budgets', 'allocation', 'allocations'],
    async execute(filters: ChatQueryFilters): Promise<PositionModel[]> {
        // The endpoint accepts a single budget-month start/end pair, so we
        // collapse multiple fiscal-year ranges into their outer bounds.
        // When the user explicitly asked for trade date, we drop the
        // budget-month filter so the two time dimensions don't intersect
        // unexpectedly (e.g. "corn trades on 2024-05-01" should match a
        // 2024-05-01 trade regardless of which budget month it belongs to).
        const ranges = filters.useTradeDate ? [] : (filters.fiscalYearRanges ?? []);
        const start = ranges.length > 0
            ? ranges.reduce((a, b) => (a.start.year! < b.start.year! || (a.start.year === b.start.year && a.start.month! <= b.start.month!) ? a : b)).start
            : undefined;
        const end = ranges.length > 0
            ? ranges.reduce((a, b) => (a.end.year! > b.end.year! || (a.end.year === b.end.year && a.end.month! >= b.end.month!) ? a : b)).end
            : undefined;

        // If the user said "trade date" / "traded on" but only gave a bare
        // year (no explicit YYYY-MM-DD), derive the trade-date window from
        // the fiscal-year range we already computed.
        let tradeStart = filters.useTradeDate ? filters.tradeDateStart : undefined;
        let tradeEnd = filters.useTradeDate ? filters.tradeDateEnd : undefined;
        if (filters.useTradeDate && !tradeStart && !tradeEnd && (filters.fiscalYearRanges?.length ?? 0) > 0) {
            const yearRanges = filters.fiscalYearRanges!;
            const minStart = yearRanges.reduce((a, b) => (a.start.year! < b.start.year! || (a.start.year === b.start.year && a.start.month! <= b.start.month!) ? a : b)).start;
            const maxEnd = yearRanges.reduce((a, b) => (a.end.year! > b.end.year! || (a.end.year === b.end.year && a.end.month! >= b.end.month!) ? a : b)).end;
            tradeStart = `${minStart.year}-${String(minStart.month).padStart(2, '0')}-01`;
            // Approximate end-of-month as day 28 to avoid month-length math.
            tradeEnd = `${maxEnd.year}-${String(maxEnd.month).padStart(2, '0')}-28`;
        }

        const response = await CommodityManagementApi.listPositions(
            null,                              // loadingStateSetter
            undefined,                         // quoteDate
            false,                             // includeExpirationDates
            true,                              // includeMarketPrices - show MTM-ish data when possible
            false,                             // useDisplayUnits
            undefined,                         // scenarioMarketPrices
            pickIds(filters.commodityIds),
            pickIds(filters.locationIds),
            pickIds(filters.counterpartyIds),
            tradeStart,
            tradeEnd,
            start?.year,
            start?.month,
            undefined,                         // budgetMonthStartFilterLabel
            end?.year,
            end?.month,
            undefined,                         // budgetMonthEndFilterLabel
            undefined, undefined, undefined,   // contractMonth start year/month/label
            undefined, undefined, undefined,   // contractMonth end year/month/label
            filters.positionTypes && filters.positionTypes.length > 0 ? filters.positionTypes : undefined,
            undefined,                         // dealTicketNumberFilter
            undefined,                         // dealTicketIdFilter
            filters.side as any,
            undefined,                         // validatedFilter
            undefined,                         // currencyFilter
            undefined,                         // currency
            undefined,                         // expiryStatus
            false,                             // includeAllocations
            false,                             // includeAllocationOffset
            filters.useTradeDate ? true : false, // filterByTradeDate - only when explicitly requested
        );
        return response.data.rows ?? [];
    },
    render(rows: unknown[], ctx: ChatQueryContext): React.ReactElement {
        const positions = rows as PositionModel[];
        // Edit and audit navigate to the full positions page so the user
        // can finish the workflow with the dedicated UI - this matches
        // the user's request that "edit should navigate to full page".
        const goToPositions = () => {
            ctx.closeDrawer?.();
            ctx.navigate('/commodity-management/positions');
        };
        return React.createElement(PositionTable, {
            customerSettings: ctx.customerSettings,
            positions,
            columnsToShow: ALL_POSITION_COLUMNS,
            onEditPosition: goToPositions,
            onSetPositionValidation: goToPositions,
            onViewAuditTrail: goToPositions,
        });
    },
    emptyMessage(filters) {
        return buildEmptyMessage('positions', filters);
    },
};

// ---------------------------------------------------------------------------
// Budgets
// ---------------------------------------------------------------------------
const budgetsDescriptor: ChatQueryDescriptor = {
    key: 'budgets',
    singular: 'budget',
    plural: 'budgets',
    keywords: ['budget', 'budgets', 'forecast', 'forecasts', 'plan', 'plans'],
    antiKeywords: ['position', 'positions', 'allocation', 'allocations'],
    async execute(filters: ChatQueryFilters): Promise<BudgetModel[]> {
        const ranges = filters.fiscalYearRanges ?? [];
        const start = ranges.length > 0
            ? ranges.reduce((a, b) => (a.start.year! < b.start.year! || (a.start.year === b.start.year && a.start.month! <= b.start.month!) ? a : b)).start
            : undefined;
        const end = ranges.length > 0
            ? ranges.reduce((a, b) => (a.end.year! > b.end.year! || (a.end.year === b.end.year && a.end.month! >= b.end.month!) ? a : b)).end
            : undefined;
        const fiscalYearsFilter = filters.fiscalYears && filters.fiscalYears.length > 0 ? filters.fiscalYears : undefined;

        const response = await CommodityManagementApi.listBudgets(
            null,                              // loadingStateSetter
            undefined,                         // quoteDate
            start?.year,
            start?.month,
            undefined,                         // budgetStartMonthLabel
            end?.year,
            end?.month,
            undefined,                         // budgetEndMonthLabel
            fiscalYearsFilter,
            pickIds(filters.commodityIds),
            pickIds(filters.locationIds),
            pickIds(filters.counterpartyIds),
            undefined,                         // budgetRoundsFilter
            false,                             // includeExpirationDates
            false,                             // includePositions
            true,                              // includeMarketPrices
            true,                              // includeBudgetRounds
            undefined,                         // expiryStatus
            undefined,                         // currency
            undefined,                         // note
        );
        return response.data.rows ?? [];
    },
    render(rows: unknown[], ctx: ChatQueryContext): React.ReactElement {
        const budgets = rows as BudgetRow[];
        const goToBudgets = () => {
            ctx.closeDrawer?.();
            ctx.navigate('/commodity-management/budgets');
        };
        return React.createElement(BudgetTable, {
            budgets,
            onViewAuditTrail: goToBudgets,
            onEditBudget: goToBudgets,
            defaultExpanded: true,
        });
    },
    emptyMessage(filters) {
        return buildEmptyMessage('budgets', filters);
    },
};

// ---------------------------------------------------------------------------
// Allocations
// ---------------------------------------------------------------------------
const allocationsDescriptor: ChatQueryDescriptor = {
    key: 'allocations',
    singular: 'allocation',
    plural: 'allocations',
    keywords: ['allocation', 'allocations', 'allotment', 'allotments', 'delivery', 'deliveries'],
    antiKeywords: ['position', 'positions', 'budget', 'budgets'],
    async execute(filters: ChatQueryFilters): Promise<AllocationModel[]> {
        const response = await CommodityManagementApi.listAllocations(
            null,                              // loadingStateSetter
            pickIds(filters.commodityIds),
            pickIds(filters.locationIds),
            pickIds(filters.counterpartyIds),
        );
        return response.data.rows ?? [];
    },
    render(rows: unknown[], ctx: ChatQueryContext): React.ReactElement {
        const allocations = rows as AllocationModel[];
        const goToAllocations = () => {
            ctx.closeDrawer?.();
            ctx.navigate('/commodity-management/allocations');
        };
        return React.createElement(AllocationTable, {
            customerSettings: ctx.customerSettings,
            allocations,
            onEditAllocation: goToAllocations,
            onViewAuditTrail: goToAllocations,
        });
    },
    emptyMessage(filters) {
        return buildEmptyMessage('allocations', filters);
    },
};

function buildEmptyMessage(plural: string, filters: ChatQueryFilters): string {
    const bits: string[] = [];
    if (filters.commodityNames?.length) bits.push(`commodity ${filters.commodityNames.join(', ')}`);
    if (filters.locationNames?.length) bits.push(`location ${filters.locationNames.join(', ')}`);
    if (filters.counterpartyNames?.length) bits.push(`counterparty ${filters.counterpartyNames.join(', ')}`);
    if (filters.fiscalYears?.length) bits.push(`fiscal year ${filters.fiscalYears.join(', ')}`);
    const suffix = bits.length > 0 ? ` for ${bits.join(', ')}` : '';
    return `I didn't find any ${plural}${suffix}.`;
}

export const CHAT_QUERY_REGISTRY: readonly ChatQueryDescriptor[] = [
    positionsDescriptor,
    budgetsDescriptor,
    allocationsDescriptor,
];

// Pick the descriptor whose keywords best match the message. Returns
// undefined when nothing scores above 0 (e.g. a greeting or upload
// instruction). The orchestrator falls back to its existing intent
// detection in that case.
export function pickQueryDescriptor(message: string): ChatQueryDescriptor | undefined {
    const lower = ` ${message.toLowerCase()} `;
    let best: { descriptor: ChatQueryDescriptor; score: number } | undefined;
    for (const descriptor of CHAT_QUERY_REGISTRY) {
        let score = 0;
        for (const keyword of descriptor.keywords) {
            if (lower.includes(` ${keyword} `) || lower.includes(`${keyword}s `) || lower.includes(` ${keyword},`)) {
                score += 2;
            } else if (lower.includes(keyword)) {
                score += 1;
            }
        }
        for (const anti of descriptor.antiKeywords ?? []) {
            if (lower.includes(anti)) score -= 1;
        }
        if (score > 0 && (!best || score > best.score)) {
            best = { descriptor, score };
        }
    }
    return best?.descriptor;
}
